import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage, auth } from "./firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  ref,
  uploadString,
  getDownloadURL,
  uploadBytes,
} from "firebase/storage";
import SignatureCanvas from "react-signature-canvas";
import heic2any from "heic2any";
import {
  ChevronLeft,
  Save,
  Camera,
  Trash2,
  Lock,
  Unlock,
  X,
  Plus,
  Calendar,
  Check,
  Undo,
  Redo,
  Eraser,
  Palette,
  Search,
  Move,
  FileText,
  User,
  Edit,
} from "lucide-react";

function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const sigCanvas = useRef(null);
  const bgCanvasRef = useRef(null);
  const canvasWrapperRef = useRef(null);

  const [visitDate, setVisitDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [visitMemo, setVisitMemo] = useState("");
  const [visitPrice, setVisitPrice] = useState("");
  const [photos, setPhotos] = useState([]);
  const [activePhotoIdx, setActivePhotoIdx] = useState(null);
  const [isCanvasLocked, setIsCanvasLocked] = useState(true);
  const [penColor, setPenColor] = useState("#ff0000");
  const [penWidth, setPenWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [imgPos, setImgPos] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  // 描き込みの履歴管理（Undo/Redo用）
  const [drawHistory, setDrawHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const [newResStart, setNewResStart] = useState("");
  const [newResEnd, setNewResEnd] = useState("");
  const [pendingResId, setPendingResId] = useState(null);
  const [viewVisit, setViewVisit] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [editVisitIdx, setEditVisitIdx] = useState(null);

  useEffect(() => {
    fetchCustomer();
  }, [id]);
  useEffect(() => {
    if (activePhotoIdx !== null) drawCurrentPhoto();
  }, [activePhotoIdx, photos, bgScale, imgPos]);

  const fetchCustomer = async () => {
    const docRef = doc(db, "customers", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.ownerId !== auth.currentUser.uid) {
        navigate("/");
        return;
      }
      setCustomer(data);
    }
  };

  const drawCurrentPhoto = () => {
    const current = photos[activePhotoIdx];
    if (!current || !bgCanvasRef.current) return;
    const canvas = bgCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ratio =
        Math.min(canvas.width / img.width, canvas.height / img.height) *
        bgScale;
      const w = img.width * ratio;
      const h = img.height * ratio;
      ctx.drawImage(
        img,
        (canvas.width - w) / 2 + imgPos.x,
        (canvas.height - h) / 2 + imgPos.y,
        w,
        h,
      );
    };
    img.src = current.raw;

    sigCanvas.current.clear();
    if (current.drawn) {
      const dImg = new Image();
      dImg.onload = () => {
        sigCanvas.current.getCanvas().getContext("2d").drawImage(dImg, 0, 0);
      };
      dImg.src = current.drawn;
    }
  };

  const switchPhoto = (idx) => {
    if (activePhotoIdx !== null) {
      const updated = [...photos];
      updated[activePhotoIdx].drawn = sigCanvas.current.getCanvas().toDataURL();
      setPhotos(updated);
    }
    setActivePhotoIdx(idx);
    setImgPos({ x: 0, y: 0 });
    setBgScale(1.0);
    setDrawHistory([]);
    setRedoStack([]);
  };

  // ★元に戻す (Undo)
  const handleUndo = () => {
    if (drawHistory.length === 0) {
      sigCanvas.current.clear();
      return;
    }
    const history = [...drawHistory];
    const current = history.pop();
    setRedoStack((prev) => [current, ...prev]); // Redoスタックに積む
    setDrawHistory(history);

    const ctx = sigCanvas.current.getCanvas().getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    if (history.length > 0) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = history[history.length - 1];
    }
  };

  // ★やり直す (Redo)
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const redo = [...redoStack];
    const next = redo.shift();
    setDrawHistory((prev) => [...prev, next]);
    setRedoStack(redo);

    const img = new Image();
    img.onload = () => {
      const ctx = sigCanvas.current.getCanvas().getContext("2d");
      ctx.drawImage(img, 0, 0);
    };
    img.src = next;
  };

  const handleProfileUpdate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const pRef = ref(
      storage,
      `profiles/${auth.currentUser.uid}/${id}_icon.jpg`,
    );
    await uploadBytes(pRef, file);
    const url = await getDownloadURL(pRef);
    await updateDoc(doc(db, "customers", id), { profileImage: url });
    fetchCustomer();
    alert("アイコン更新完了");
  };

  const saveKarte = async () => {
    try {
      let currentPhotos = [...photos];
      if (activePhotoIdx !== null) {
        currentPhotos[activePhotoIdx].drawn = sigCanvas.current
          .getCanvas()
          .toDataURL();
      }

      const uploadedImages = await Promise.all(
        currentPhotos.map(async (p, i) => {
          const rawUrl = p.raw.startsWith("http")
            ? p.raw
            : await (async () => {
                const r = ref(
                  storage,
                  `kartes/${id}_raw_${Date.now()}_${i}.png`,
                );
                await uploadString(r, p.raw, "data_url");
                return getDownloadURL(r);
              })();
          const drawnUrl = await (async () => {
            const r = ref(storage, `kartes/${id}_drawn_${Date.now()}_${i}.png`);
            await uploadString(
              r,
              p.drawn || sigCanvas.current.getCanvas().toDataURL(),
              "data_url",
            );
            return getDownloadURL(r);
          })();
          return { raw: rawUrl, drawn: drawnUrl };
        }),
      );

      const newRecord = {
        date: visitDate,
        memo: visitMemo,
        price: Number(visitPrice) || 0,
        images: uploadedImages,
        createdAt: new Date().toISOString(),
        isCancellation: false,
      };

      let updatedVisits = [...(customer.visits || [])];
      if (editVisitIdx !== null) updatedVisits[editVisitIdx] = newRecord;
      else updatedVisits = [newRecord, ...updatedVisits];

      let updatedRes = [...(customer.reservations || [])];
      if (pendingResId)
        updatedRes = updatedRes.filter((r) => r.id !== pendingResId);

      await updateDoc(doc(db, "customers", id), {
        visits: updatedVisits,
        reservations: updatedRes,
      });
      alert("保存完了");
      fetchCustomer();
      setVisitMemo("");
      setVisitPrice("");
      setPhotos([]);
      setActivePhotoIdx(null);
      setEditVisitIdx(null);
    } catch (e) {
      alert("エラー: " + e.message);
    }
  };

  if (!customer)
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>読み込み中...</div>
    );

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button onClick={() => navigate("/")} style={styles.backBtn}>
            <ChevronLeft size={18} /> 戻る
          </button>
          <label style={styles.headerIconWrap}>
            {customer.profileImage ? (
              <img
                src={customer.profileImage}
                style={styles.headerIcon}
                alt="p"
              />
            ) : (
              <User color="#94a3b8" size={20} />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleProfileUpdate}
              style={{ display: "none" }}
            />
          </label>
          <span style={styles.headerName}>{customer.name} 様</span>
        </div>
        <button onClick={saveKarte} style={styles.saveBtn}>
          <Save size={16} /> 保存
        </button>
      </header>

      <main style={styles.main}>
        <div style={styles.layoutWrapper}>
          <section style={styles.leftCol}>
            <div style={styles.toolbar}>
              <button
                onClick={() => setIsCanvasLocked(!isCanvasLocked)}
                style={{
                  ...styles.toolBtn,
                  backgroundColor: isCanvasLocked ? "#f1f5f9" : "#0ea5e9",
                  color: isCanvasLocked ? "#64748b" : "#fff",
                }}
              >
                {isCanvasLocked ? (
                  <>
                    <Lock size={14} /> 閲覧中
                  </>
                ) : (
                  <>
                    <Unlock size={14} /> 筆記中
                  </>
                )}
              </button>
              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <button
                  onClick={handleUndo}
                  title="元に戻す"
                  style={styles.iconBtn}
                >
                  <Undo size={18} />
                </button>
                <button
                  onClick={handleRedo}
                  title="やり直す"
                  style={styles.iconBtn}
                >
                  <Redo size={18} />
                </button>
                <button
                  onClick={() => setIsEraser(!isEraser)}
                  style={{
                    ...styles.iconBtn,
                    color: isEraser ? "#0ea5e9" : "#64748b",
                  }}
                >
                  <Eraser size={18} />
                </button>
                <button
                  onClick={() => {
                    sigCanvas.current.clear();
                    setDrawHistory([]);
                  }}
                  style={styles.iconBtn}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={styles.toolItem}>
                <Palette size={14} />
                <input
                  type="color"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  style={styles.colorPicker}
                />
              </div>
              <div style={styles.toolItem}>
                <Search size={14} />
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.1"
                  value={bgScale}
                  onChange={(e) => setBgScale(parseFloat(e.target.value))}
                  style={{ width: "60px" }}
                />
              </div>
              <button
                onClick={() => setIsMoveMode(!isMoveMode)}
                style={{
                  ...styles.toolBtn,
                  backgroundColor: isMoveMode ? "#0ea5e9" : "#f1f5f9",
                  color: isMoveMode ? "#fff" : "#64748b",
                }}
              >
                <Move size={14} /> {isMoveMode ? "移動" : "描画"}
              </button>
            </div>

            <div
              ref={canvasWrapperRef}
              style={styles.canvasFrame}
              onMouseMove={(e) => {
                if (isDragging && isMoveMode)
                  setImgPos({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y,
                  });
                const r = canvasWrapperRef.current.getBoundingClientRect();
                setCursorPos({ x: e.clientX - r.left, y: e.clientY - r.top });
              }}
              onMouseDown={(e) => {
                if (!isCanvasLocked && isMoveMode) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - imgPos.x,
                    y: e.clientY - imgPos.y,
                  });
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
            >
              <canvas
                ref={bgCanvasRef}
                style={styles.bgCanvas}
                width={400}
                height={533}
              />
              <div
                style={{
                  ...styles.sigLayer,
                  pointerEvents: isCanvasLocked || isMoveMode ? "none" : "auto",
                }}
              >
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor={isEraser ? "rgba(0,0,0,0)" : penColor}
                  minWidth={penWidth}
                  maxWidth={penWidth}
                  onEnd={() => {
                    setDrawHistory((p) => [
                      ...p,
                      sigCanvas.current.getCanvas().toDataURL(),
                    ]);
                    setRedoStack([]); // 新しく描いたらRedoスタックは消去
                  }}
                  canvasProps={{ style: styles.sigCanvas }}
                />
              </div>
              {!isCanvasLocked && !isMoveMode && (
                <div
                  style={{
                    ...styles.penCursor,
                    left: cursorPos.x,
                    top: cursorPos.y,
                    width: penWidth,
                    height: penWidth,
                    backgroundColor: penColor,
                  }}
                />
              )}
            </div>

            <div style={styles.photoBox}>
              <label style={styles.addBtn}>
                <Camera size={18} /> 写真追加・撮影
                <input
                  type="file"
                  multiple
                  accept="image/*,.heic"
                  onChange={async (e) => {
                    for (let f of Array.from(e.target.files)) {
                      let file = f;
                      if (f.name.toLowerCase().endsWith(".heic")) {
                        const blob = await heic2any({
                          blob: f,
                          toType: "image/jpeg",
                          quality: 0.7,
                        });
                        file = new File([blob], "p.jpg", {
                          type: "image/jpeg",
                        });
                      }
                      const r = new FileReader();
                      r.onload = (ev) => {
                        const newP = {
                          id: Date.now() + Math.random(),
                          raw: ev.target.result,
                          drawn: null,
                        };
                        setPhotos((prev) => [...prev, newP]);
                        if (activePhotoIdx === null) setActivePhotoIdx(0);
                      };
                      r.readAsDataURL(file);
                    }
                  }}
                  style={{ display: "none" }}
                />
              </label>
              <div style={styles.thumbList}>
                {photos.map((p, i) => (
                  <div
                    key={p.id}
                    style={{
                      ...styles.thumbWrap,
                      border:
                        activePhotoIdx === i ? "3px solid #0ea5e9" : "none",
                    }}
                    onClick={() => switchPhoto(i)}
                  >
                    <img src={p.raw} style={styles.thumb} alt="t" />
                    <X
                      size={14}
                      style={styles.delImg}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotos((prev) => prev.filter((_, idx) => idx !== i));
                        setActivePhotoIdx(null);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <div style={styles.rightCol}>
            <section style={styles.card}>
              <h3 style={styles.cardTitle}>来店内容</h3>
              <div style={styles.rowStack}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={styles.label}>来店日</label>
                  <input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={styles.label}>売上(¥)</label>
                  <input
                    type="number"
                    value={visitPrice}
                    onChange={(e) => setVisitPrice(e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>
              <textarea
                value={visitMemo}
                onChange={(e) => setVisitMemo(e.target.value)}
                placeholder="メモ"
                style={styles.textarea}
              />
            </section>

            <section style={{ ...styles.card, border: "2px solid #0ea5e9" }}>
              <h3 style={{ ...styles.cardTitle, color: "#0ea5e9" }}>
                <Calendar size={18} /> 予約管理
              </h3>
              <div style={styles.resForm}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>開始</label>
                  <input
                    type="datetime-local"
                    value={newResStart}
                    onChange={(e) => setNewResStart(e.target.value)}
                    style={styles.inputSmall}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>終了</label>
                  <input
                    type="datetime-local"
                    value={newResEnd}
                    onChange={(e) => setNewResEnd(e.target.value)}
                    style={styles.inputSmall}
                  />
                </div>
              </div>
              <button
                onClick={async () => {
                  const res = {
                    id: Date.now(),
                    start: newResStart,
                    end: newResEnd,
                  };
                  const up = [...(customer.reservations || []), res];
                  await updateDoc(doc(db, "customers", id), {
                    reservations: up,
                  });
                  fetchCustomer();
                  setNewResStart("");
                  setNewResEnd("");
                }}
                style={{
                  ...styles.addResBtn,
                  width: "100%",
                  marginTop: "10px",
                }}
              >
                <Plus size={16} /> 予約追加
              </button>
              <div style={styles.resList}>
                {(customer.reservations || []).map((r) => (
                  <div key={r.id} style={styles.resItem}>
                    <span>
                      {r.start.replace("T", " ")} 〜{" "}
                      {r.end?.split("T")[1] || ""}
                    </span>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <button
                        onClick={() => {
                          setVisitDate(r.start.split("T")[0]);
                          setPendingResId(r.id);
                        }}
                        style={styles.checkBtn}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          const up = customer.reservations.filter(
                            (x) => x.id !== r.id,
                          );
                          await updateDoc(doc(db, "customers", id), {
                            reservations: up,
                          });
                          fetchCustomer();
                        }}
                        style={styles.delResBtn}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={styles.historyArea}>
              <h3 style={styles.cardTitle}>履歴</h3>
              {(showAllHistory
                ? customer.visits || []
                : (customer.visits || []).slice(0, 3)
              ).map((v, i) => (
                <div key={i} style={styles.historyCard}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <strong
                      onClick={() => setViewVisit(v)}
                      style={{ cursor: "pointer" }}
                    >
                      {v.date} (¥{v.price.toLocaleString()})
                    </strong>
                    <Edit
                      size={16}
                      color="#0ea5e9"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setVisitDate(v.date);
                        setVisitMemo(v.memo);
                        setVisitPrice(v.price);
                        const editPhotos = (v.images || []).map((img, idx) => ({
                          id: idx,
                          raw: img.raw,
                          drawn: img.drawn,
                        }));
                        setPhotos(editPhotos);
                        setActivePhotoIdx(editPhotos.length > 0 ? 0 : null);
                        setEditVisitIdx(i);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    />
                  </div>
                  <p
                    onClick={() => setViewVisit(v)}
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                  >
                    {v.memo}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      marginTop: "5px",
                      overflowX: "auto",
                    }}
                  >
                    {(v.images || []).map((img, j) => (
                      <div
                        key={j}
                        style={styles.historyThumbOverlay}
                        onClick={() => setViewVisit(v)}
                      >
                        <img
                          src={img.raw}
                          style={styles.historyThumbBg}
                          alt="b"
                        />
                        <img
                          src={img.drawn}
                          style={styles.historyThumbDrawn}
                          alt="d"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!showAllHistory && customer.visits?.length > 3 && (
                <button
                  onClick={() => setShowAllHistory(true)}
                  style={styles.moreBtn}
                >
                  もっと見る
                </button>
              )}
            </section>
          </div>
        </div>
      </main>

      {viewVisit && (
        <div style={styles.modalOverlay} onClick={() => setViewVisit(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>{viewVisit.date} 詳細</h3>
              <X
                size={24}
                onClick={() => setViewVisit(null)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div style={styles.modalBody}>
              <p
                style={{
                  backgroundColor: "#f1f5f9",
                  padding: "10px",
                  borderRadius: "10px",
                  marginBottom: "15px",
                }}
              >
                {viewVisit.memo || "メモなし"}
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                {(viewVisit.images || []).map((img, idx) => (
                  <div key={idx} style={styles.modalImgWrap}>
                    <img src={img.raw} style={styles.modalImgBg} alt="raw" />
                    <img
                      src={img.drawn}
                      style={styles.modalImgDrawn}
                      alt="drawn"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 15px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #f1f5f9",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  headerIconWrap: {
    width: "32px",
    height: "32px",
    borderRadius: "10px",
    backgroundColor: "#f1f5f9",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    cursor: "pointer",
    border: "1px solid #e2e8f0",
  },
  headerIcon: { width: "100%", height: "100%", objectFit: "cover" },
  headerName: { fontSize: "16px", fontWeight: "bold" },
  backBtn: {
    border: "none",
    background: "none",
    display: "flex",
    alignItems: "center",
    color: "#64748b",
    fontWeight: "bold",
    cursor: "pointer",
  },
  saveBtn: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "10px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  main: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  layoutWrapper: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "20px",
  },
  leftCol: {
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  canvasFrame: {
    position: "relative",
    width: "100%",
    aspectRatio: "3/4",
    backgroundColor: "#fff",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
    cursor: "crosshair",
  },
  bgCanvas: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 1,
  },
  sigLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 2,
  },
  sigCanvas: { width: "100%", height: "100%" },
  penCursor: {
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
    border: "1px solid #000",
    zIndex: 10,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  toolBtn: {
    border: "none",
    padding: "8px 12px",
    borderRadius: "10px",
    fontWeight: "bold",
    fontSize: "13px",
    cursor: "pointer",
  },
  iconBtn: {
    border: "none",
    background: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: "5px",
  },
  toolItem: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    backgroundColor: "#fff",
    padding: "5px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  colorPicker: {
    border: "none",
    width: "24px",
    height: "24px",
    padding: 0,
    background: "none",
    cursor: "pointer",
  },
  photoBox: { marginTop: "15px" },
  addBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "15px",
    backgroundColor: "#fff",
    border: "2px dashed #0ea5e9",
    color: "#0ea5e9",
    borderRadius: "15px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  thumbList: {
    display: "flex",
    gap: "10px",
    marginTop: "10px",
    overflowX: "auto",
    paddingBottom: "5px",
  },
  thumbWrap: {
    position: "relative",
    flexShrink: 0,
    borderRadius: "8px",
    overflow: "hidden",
  },
  thumb: {
    width: "60px",
    height: "80px",
    objectFit: "contain", // ★ cover から contain に変更
    backgroundColor: "#f1f5f9", // 余白が出るので背景色を追加
    border: "1px solid #e2e8f0",
  },
  delImg: {
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "#ef4444",
    color: "#fff",
    borderRadius: "0 0 0 8px",
    padding: "2px",
  },
  rightCol: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    boxSizing: "border-box",
  },
  cardTitle: {
    fontSize: "15px",
    fontWeight: "bold",
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  rowStack: { display: "flex", gap: "10px", flexWrap: "wrap" },
  label: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "bold",
    marginBottom: "3px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    boxSizing: "border-box",
    minWidth: 0, // 突き抜け防止
    backgroundColor: "#fff", // グレーの背景を白に固定
    WebkitAppearance: "none", // iPhone特有のスタイルを解除
  },
  textarea: {
    width: "100%",
    minHeight: "80px",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    boxSizing: "border-box",
    marginTop: "10px",
  },
  resForm: { display: "flex", gap: "5px", flexWrap: "wrap" },
  inputSmall: {
    flex: 1,
    minWidth: "120px",
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "11px",
    boxSizing: "border-box",
  },
  addResBtn: {
    backgroundColor: "#0ea5e9",
    color: "#fff",
    border: "none",
    padding: "10px",
    borderRadius: "8px",
    cursor: "pointer",
  },
  resList: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    marginTop: "10px",
  },
  resItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    borderRadius: "10px",
    backgroundColor: "#f0f9ff",
    fontSize: "12px",
  },
  checkBtn: {
    background: "#d1fae5",
    border: "none",
    padding: "5px",
    borderRadius: "5px",
    color: "#10b981",
    cursor: "pointer",
  },
  delResBtn: {
    background: "#fee2e2",
    border: "none",
    padding: "5px",
    borderRadius: "5px",
    color: "#ef4444",
    cursor: "pointer",
  },
  historyCard: {
    backgroundColor: "#fff",
    padding: "12px",
    borderRadius: "15px",
    border: "1px solid #e2e8f0",
    marginBottom: "10px",
  },
  historyThumbOverlay: {
    position: "relative",
    width: "50px",
    height: "60px",
    flexShrink: 0,
    borderRadius: "5px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
  },
  historyThumbBg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain", // ★ cover から contain に変更
  },
  historyThumbDrawn: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  moreBtn: {
    width: "100%",
    padding: "10px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: "10px",
    color: "#64748b",
    cursor: "pointer",
    fontWeight: "bold",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.85)", // 背景を少し濃くして中身を強調
    zIndex: 2000,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "20px",
    paddingTop: "50px", // 上端からの距離を少し広げた
    boxSizing: "border-box",
  },
  modalContent: {
    backgroundColor: "#fff",
    width: "95%",
    maxWidth: "600px",
    borderRadius: "30px", // 角の丸みを少し強くしてゆったり感を演出
    maxHeight: "85vh",
    overflowY: "auto",
    boxSizing: "border-box",
    position: "relative",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)", // 影を深くして立体感を
  },
  modalHeader: {
    padding: "30px 25px 15px 25px", // 全体的に余白をアップ（特に上）
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalBody: {
    padding: "25px", // 内側の余白を20pxから25pxへ拡大
    display: "flex",
    flexDirection: "column",
    gap: "20px", // メモと写真の間の隙間をしっかり確保
  },
  modalImgWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "3/4",
    borderRadius: "15px", // 写真自体の丸みも少しアップ
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    boxSizing: "border-box",
  },
  modalImgBg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    zIndex: 1, // ★背景を下に
  },
  modalImgDrawn: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "fill", // ★ 枠いっぱいに広げて背景と位置を合わせる
    zIndex: 2, // ★描き込みを上に
    pointerEvents: "none",
  },
};

export default CustomerDetail;
